// src/components/Register.js
import React, { useState } from "react";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
} from "firebase/auth";
import {
  TextField,
  Button,
  Container,
  MenuItem,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase-config";
import BagTag from "./Profile/BagTag"; // Reuse the BagTag component
import InfoIcon from "@mui/icons-material/Info";

const Register = ({ onRegisterSuccess }) => {
  // Authentication fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // First Name and Last Name fields (now required)
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [location, setLocation] = useState("");
  // Handicap remains a string for editing but will be converted upon saving.
  const [handicap, setHandicap] = useState("");
  const [gender, setGender] = useState("");

  // New state for Bag Tag (our unique identifier)
  const [bagTag, setBagTag] = useState("");

  // State for displaying the info dialog popup
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  // Snackbar state for notifications.
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  // Shared sx styles for input labels.
  const inputLabelSx = {
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "unset",
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRegister = async () => {
    // Validate Bag Tag.
    if (!bagTag.trim()) {
      setSnackbarMessage("Bag Tag is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    const bagTagRegex = /^[a-zA-Z0-9_.]+$/;
    if (!bagTagRegex.test(bagTag.trim())) {
      setSnackbarMessage(
        "Invalid Bag Tag. Only letters, numbers, underscores, and periods are allowed (no spaces)."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    // Validate Email.
    if (!email.trim()) {
      setSnackbarMessage("Email is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    // Validate Email with regex.
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setSnackbarMessage("Please enter a valid email address.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    // Validate Password.
    if (!password) {
      setSnackbarMessage("Password is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    // Validate First Name.
    if (!firstName.trim()) {
      setSnackbarMessage("First Name is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    // Validate Last Name.
    if (!lastName.trim()) {
      setSnackbarMessage("Last Name is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    // Ensure passwords match.
    if (password !== confirmPassword) {
      setSnackbarMessage("Passwords do not match. Please re-enter your password.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      // Create a new user with email and password via Firebase.
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Save additional user info to Firestore.
      await setDoc(doc(db, "users", user.uid), {
        email: user.email,
        firstName: firstName,
        firstNameLower: firstName.toLowerCase(),
        lastName: lastName,
        lastNameLower: lastName.toLowerCase(),
        location: location || null,
        // Convert handicap to a number if provided.
        handicap: handicap.trim() === "" ? null : parseFloat(handicap),
        gender: gender || null,
        bagTag: bagTag.toLowerCase(),
        createdAt: new Date().toISOString(),
        isAdmin: false, // All new users are not admins by default.
        isHandicapApproved: false,
      });

      // Send a verification email.
      await sendEmailVerification(user);

      // Notify parent component.
      if (onRegisterSuccess) {
        onRegisterSuccess();
      }

      // Sign out the user.
      await signOut(auth);
    } catch (error) {
      console.error("Registration failed:", error);
      setSnackbarMessage("Error creating account. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  return (
    <Container>
      {/* Bag Tag Component */}
      <BagTag value={bagTag} onChange={setBagTag} />

      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ sx: inputLabelSx }}
      />

      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ sx: inputLabelSx }}
      />

      <TextField
        label="Confirm Password"
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ sx: inputLabelSx }}
      />

      <TextField
        label="First Name"
        type="text"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        required
        fullWidth
        margin="normal"
        InputLabelProps={{
          sx: { ...inputLabelSx, "& .MuiInputLabel-asterisk": { display: "none" } },
        }}
      />

      <TextField
        label="Last Name"
        type="text"
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        required
        fullWidth
        margin="normal"
        InputLabelProps={{
          sx: { ...inputLabelSx, "& .MuiInputLabel-asterisk": { display: "none" } },
        }}
      />

      <TextField
        label="Location (optional)"
        type="text"
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ sx: inputLabelSx }}
      />

      <TextField
        label="Handicap (optional)"
        type="number"
        value={handicap}
        onChange={(e) => setHandicap(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ sx: inputLabelSx }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip
                title="This is an unofficial handicap. Follow the process in Profile to request for an Official Handicap."
                arrow
              >
                <InfoIcon
                  fontSize="small"
                  onClick={() => setInfoDialogOpen(true)}
                  style={{ cursor: "pointer" }}
                />
              </Tooltip>
            </InputAdornment>
          ),
          inputProps: {
            style: {
              WebkitAppearance: "none",
              MozAppearance: "textfield",
            },
          },
        }}
        sx={{
          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
            display: "none",
          },
        }}
      />

      <TextField
        label="Gender (optional)"
        select
        value={gender}
        onChange={(e) => setGender(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ sx: inputLabelSx }}
      >
        <MenuItem value="male">Male</MenuItem>
        <MenuItem value="female">Female</MenuItem>
      </TextField>

      {/* Wrap the Register button in a Box to align it to the right */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button variant="contained" color="primary" onClick={handleRegister}>
          Register
        </Button>
      </Box>

      {/* Info Dialog Popup */}
      <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)}>
        <DialogTitle>Handicap Information</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This is an unofficial handicap. Follow the process in Profile to request for an Official Handicap.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Register;